import PropTypes from 'prop-types';
import React, { useState, useCallback } from 'react';
import styled from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import Card from 'components/Card';
import DataTable from 'components/DataTable';
import { DeleteConfirmModal } from 'components/FormModal';
import SearchInput from 'components/SearchInput';
import TableFilter from 'components/TableFilter';
import VideoModal from '../../LearningHub/components/VideoModal';
import VideoFormModal from '../components/VideoFormModal';
import {
  useGetAdminVideos,
  useGetAdminTopics,
  useCreateVideo,
  useUpdateVideo,
  useDeleteVideo,
} from '../gql';
import {
  PageHeader,
  PageTitle,
  PageDescription,
  StatusBadge,
  ResultsCount,
} from '../StyledComponents';

const TableHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 24px;
  gap: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const ContentWrapper = styled.div`
  padding: 20px;
`;

const filterConfig = [
  {
    key: 'mode',
    label: 'Mode',
    type: 'radio',
    options: [
      { label: 'Ideas & Tips', value: 'discover' },
      { label: 'How To Do It', value: 'howto' },
    ],
  },
  {
    key: 'status',
    label: 'Status',
    type: 'radio',
    options: [
      { label: 'Published', value: 'published' },
      { label: 'Draft', value: 'draft' },
    ],
  },
];

const ITEMS_PER_PAGE = 15;

const VideosPageV2 = () => {
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ mode: '', status: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [previewVideo, setPreviewVideo] = useState(null);
  const [deletingVideo, setDeletingVideo] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const queryInput = {
    ...(search ? { search } : {}),
    ...(filters.mode ? { mode: filters.mode } : {}),
    ...(filters.status ? { status: filters.status } : {}),
    page: currentPage,
    limit: ITEMS_PER_PAGE,
  };

  const { videos, count, loading } = useGetAdminVideos(queryInput);
  const totalPages = Math.ceil(count / ITEMS_PER_PAGE);
  const { topics } = useGetAdminTopics();
  const createVideo = useCreateVideo();
  const updateVideo = useUpdateVideo();
  const deleteVideo = useDeleteVideo();

  const getTopicName = (video) => video.topic?.name || '';

  const handleSearch = useCallback((term) => {
    setSearch(term);
    setCurrentPage(1);
  }, []);

  const handleApplyFilters = useCallback((newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  }, []);

  const handleClearFilters = useCallback((clearedFilters) => {
    setFilters(clearedFilters);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback(
    (page) => {
      if (page < 1 || page > totalPages) return;
      setCurrentPage(page);
    },
    [totalPages]
  );

  const handleSave = async (data) => {
    const input = {
      topic_id: data.topic_id,
      mode: data.mode,
      title: data.title,
      description: data.description,
      vimeo_id: data.vimeo_id,
      duration_label: data.duration_label,
      status: data.status,
      is_new: data.is_new,
      is_featured: data.is_featured,
      help_link: data.help_link || null,
    };

    if (editingVideo) {
      await updateVideo({ id: editingVideo.id, ...input });
    } else {
      await createVideo(input);
    }
    setShowModal(false);
    setEditingVideo(null);
  };

  const handleEdit = (video) => {
    setEditingVideo(video);
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingVideo(null);
    setShowModal(true);
  };

  const handleDeleteClick = (video) => {
    setDeletingVideo(video);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    await deleteVideo(deletingVideo.id);
    setIsDeleting(false);
    setDeletingVideo(null);
  };

  const columns = [
    {
      field: 'title',
      header: 'Video',
      render: (video) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <img
            src={`https://vumbnail.com/${video.vimeo_id}.jpg`}
            alt=""
            style={{
              width: '80px',
              height: '48px',
              borderRadius: '6px',
              objectFit: 'cover',
              flexShrink: 0,
            }}
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
          <div>
            <div
              style={{
                fontWeight: 600,
                marginBottom: '4px',
              }}
            >
              {video.title}
            </div>
            <div
              style={{
                fontSize: '14px',
                color: 'var(--text-secondary)',
              }}
            >
              {video.description}
            </div>
          </div>
        </div>
      ),
    },
    {
      field: 'mode',
      header: 'Mode',
      render: (video) => (
        <StatusBadge variant={video.mode}>
          {video.mode === 'discover' ? 'Ideas & Tips' : 'How To Do It'}
        </StatusBadge>
      ),
    },
    {
      field: 'topic',
      header: 'Topic',
      render: (video) => getTopicName(video),
    },
    {
      field: 'duration',
      header: 'Duration',
      render: (video) => video.duration_label,
    },
    {
      field: 'badges',
      header: 'Badges',
      render: (video) => (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {video.is_new && <StatusBadge variant="new">NEW</StatusBadge>}
          {video.is_featured && (
            <StatusBadge variant="featured">Featured</StatusBadge>
          )}
        </div>
      ),
    },
    {
      field: 'status',
      header: 'Status',
      render: (video) => (
        <StatusBadge variant={video.status}>
          {video.status === 'published' ? 'Published' : 'Draft'}
        </StatusBadge>
      ),
    },
  ];

  const previewVideoTransformed = previewVideo
    ? {
        id: String(previewVideo.id),
        title: previewVideo.title,
        description: previewVideo.description,
        duration: previewVideo.duration_label,
        vimeoId: previewVideo.vimeo_id,
        isNew: previewVideo.is_new,
        helpLink: previewVideo.help_link,
      }
    : null;

  const actions = [
    {
      type: 'view',
      title: 'Preview',
      onClick: (video) => setPreviewVideo(video),
    },
    {
      type: 'edit',
      title: 'Edit',
      onClick: handleEdit,
    },
    {
      type: 'delete',
      title: 'Delete',
      onClick: handleDeleteClick,
    },
  ];

  return (
    <>
      <PageHeader>
        <div>
          <PageTitle>Videos</PageTitle>
          <PageDescription>Manage learning hub video content</PageDescription>
        </div>
        <div style={{ minWidth: '160px' }}>
          <ButtonV2 onClick={handleAdd}>Add Video</ButtonV2>
        </div>
      </PageHeader>

      <Card noPadding>
        <ContentWrapper>
          <TableHeader>
            <SearchInput
              onSearch={handleSearch}
              placeholder="Search videos..."
              debounceMs={400}
            />
            <TableFilter
              filters={filterConfig}
              values={filters}
              onApply={handleApplyFilters}
              onClear={handleClearFilters}
            />
          </TableHeader>

          <DataTable
            columns={columns}
            data={videos}
            keyField="id"
            actions={actions}
            loading={loading}
            emptyMessage="No videos found"
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />

          <ResultsCount>
            Showing {videos.length} of {count} videos
          </ResultsCount>
        </ContentWrapper>
      </Card>

      {showModal && (
        <VideoFormModal
          video={editingVideo}
          topics={topics}
          onSave={handleSave}
          onClose={() => {
            setShowModal(false);
            setEditingVideo(null);
          }}
        />
      )}

      <VideoModal
        video={previewVideoTransformed}
        onClose={() => setPreviewVideo(null)}
        mode={previewVideo?.mode}
      />

      <DeleteConfirmModal
        isOpen={!!deletingVideo}
        onClose={() => setDeletingVideo(null)}
        title="Delete Video?"
        message={`Are you sure you want to delete ${deletingVideo?.title}? This action cannot be undone.`}
        onConfirm={handleDeleteConfirm}
        isDeleting={isDeleting}
      />
    </>
  );
};

VideosPageV2.propTypes = {
  routePrefix: PropTypes.string,
};

export default VideosPageV2;
